import { createI18n } from "vue-i18n"
import { getLangPack, getLangConfig, getLangConfigMain } from "../api/api"
import bus from "../lib/bus"
import { platformStore } from "@/store/index"

/**
 * 判断当前是否从pay路由进入
 * @returns {boolean} 是否从pay路由进入
 */
function isFromPayRoute() {
  // 检查companyId是从路由传参代入
  const store = platformStore()
  const companyId = store.companyId
  return !!companyId
}

/**
 * 获取语言配置并设置到localStorage（主路由使用）
 * @returns {Promise<Object>} 语言配置响应
 */
async function getConfig() {
  const store = platformStore()
  const res = await getLangConfig()
  if (res.code === 200) {
    const langList = res.metadata.list

    // 只有在没有设置过语言的情况下才设置默认语言
    if (!store.lang) {
      store.changeLang(langList[0].lang_const)
    }

    // 只有在没有设置过companyId的情况下才设置
    if (!store.companyId) {
      store.setCompanyId(langList[0].company_id)
    }

    // 将语言列表存储，供组件使用
    store.setAvailableLanguages(langList)
    // 触发事件通知组件更新语言列表
    bus.$emit("updateLanguageList")
  }
  return res
}

/**
 * 获取公司主体信息（pay路由使用）
 */
async function getConfigMain() {
  const store = platformStore()
  const companyId = store.companyId
  const res = await getLangConfigMain(companyId)
  if (res.code === 200) {
    const langInfoList = res.metadata
    if (langInfoList.cs_url) {
      store.setKfUrl(langInfoList.cs_url)
    } else {
      store.setKfUrl("")
    }
    // 只有在没有设置过语言的情况下才设置默认语言
    if (!store.lang) {
      store.changeLang(langInfoList.lang[0].lang_const)
    }
    if (langInfoList.lang.length == 1) {
      // 针对东南亚仙剑混服场景，强制设置语言为唯一语言
      const langCode = langInfoList.lang[0].lang_const
      store.changeLang(langCode)

      // 更新语言包
      if (window.$i18n) {
        try {
          // await reloadLanguagePack(langCode, window.$i18n)
          // 更新i18n当前语言
          window.$i18n.locale.value = langCode
        } catch (error) {
          console.error("强制设置语言时更新语言包失败:", error)
        }
      }
    }
    // 将语言列表存储，供组件使用
    store.setAvailableLanguages(langInfoList.lang)

    // 触发事件通知组件更新语言列表
    bus.$emit("updateLanguageList")
  }
  return res
}

/**
 * 获取语言包URL
 * @returns {Promise<string>} 语言包URL
 */
async function getLanguage() {
  // 确保companyId已经设置
  const store = platformStore()
  const companyId = store.companyId
  if (!companyId) {
    throw new Error("companyId未设置，请先调用getConfig()或getConfigMain()")
  }
  const res = await getLangPack()
  // 修复语言老配置问题，例如en，enUS
  if (store.lang && store.lang.length < 5) {
    store.changeLang(res.metadata.language)
  }
  store.setLogo(res.metadata.company.logo)
  store.setWebIcon(res.metadata.company.desktop_icon)
  if (res.metadata.company.cs_url) {
    store.setKfUrl(res.metadata.company.cs_url)
  } else {
    store.setKfUrl("")
  }
  return res.metadata.url
}

/**
 * 异步获取语言包
 * @returns {Promise<Object>} 语言包数据
 */
async function loadLanguagePackages(forceRefresh = false) {
  // 检查是否已经有配置信息
  const store = platformStore()
  const hasConfig = store.companyId && store.availableLanguages

  // 只有在没有配置信息时才获取配置
  if (!hasConfig || forceRefresh) {
    // 根据路由来源选择不同的配置获取方法
    if (isFromPayRoute()) {
      // 检查companyId是从路由传参代入，使用getConfigMain
      await getConfigMain()
    } else {
      // 从主路由进入，使用getConfig
      await getConfig()
    }
  }

  // 然后获取语言包URL
  const langUrl = await getLanguage()
  // 使用 fetch 获取 JSON 文件内容
  try {
    const response = await fetch(langUrl)
    const languageData = await response.json()

    // 根据当前语言设置返回正确的格式
    const currentLang = store.lang || navigator.language.toLowerCase()

    // 返回 vue-i18n 需要的格式：{ 'ja-jp': { 翻译内容 } }
    return {
      [currentLang]: languageData,
    }
  } catch (error) {
    // 返回空的语言包以防止错误
    return {
      "ja-jp": {},
      "zh-cn": {},
      "en-us": {},
      "ko-kr": {},
      "vi-vn": {},
      "th-th": {},
      "zh-tw": {},
      INA: {},
    }
  }
}

/**
 * 重新初始化多语言配置的执行标志位
 * @type {boolean}
 */
let isReinitializing = false

/**
 * 重新初始化多语言配置
 * @param {Object} i18n - i18n实例
 * @returns {Promise<void>}
 */
async function reinitializeI18n(i18n, forceRefresh = false) {
  // 如果正在执行，则跳过
  if (isReinitializing) {
    console.log("reinitializeI18n is already running, skip")
    return
  }

  isReinitializing = true
  const store = platformStore()
  try {
    const messages = await loadLanguagePackages(forceRefresh)
    // 更新i18n实例的messages
    Object.keys(messages).forEach((locale) => {
      i18n.global.setLocaleMessage(locale, messages[locale])
    })

    // 保持当前语言设置，不强制更新
    const currentLang =
      i18n.global.locale.value || store.lang || navigator.language.toLowerCase()
    if (currentLang !== i18n.global.locale.value) {
      i18n.global.locale.value = currentLang
    }
    // 触发事件通知组件更新语言列表
    bus.$emit("updateLanguageList")
  } catch (error) {
    console.error(error)
  } finally {
    // 延迟重置标志位，避免短时间内重复执行
    setTimeout(() => {
      isReinitializing = false
    }, 300)
  }
}

/**
 * 重新加载指定语言的语言包
 */
async function reloadLanguagePack(langCode, i18n) {
  console.log("getLangPack", 223)
  try {
    // 获取语言包URL
    const res = await getLangPack()
    if (res.code === 200) {
      const langUrl = res.metadata.url

      // 获取语言包内容
      const response = await fetch(langUrl)
      const languageData = await response.json()

      // 更新i18n实例的语言包
      if (i18n && i18n.global) {
        i18n.global.setLocaleMessage(langCode, languageData)
      } else if (window.$i18n) {
        window.$i18n.setLocaleMessage(langCode, languageData)
      }
    } else {
      throw new Error("获取语言包URL失败")
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

/**
 * 监听companyId变化
 * @param {Object} i18n - i18n实例
 */
function watchCompanyIdChange(i18n) {
  const store = platformStore()
  let lastCompanyId = store.companyId
  // 使用 Pinia 的 $subscribe 监听 store 中 companyId 的变化
  store.$subscribe((mutation, state) => {
    const currentCompanyId = state.companyId
    // 当 companyId 发生变化时，重新初始化多语言配置
    if (currentCompanyId != lastCompanyId) {
      lastCompanyId = currentCompanyId
      // 延迟执行，确保 store 更新完成
      setTimeout(() => {
        reinitializeI18n(i18n)
      }, 100)
    }
  })

  // 监听storage事件（跨标签页同步）
  // 当其他标签页修改 localStorage 时，需要手动同步到 store
  window.addEventListener("storage", (e) => {
    if (e.key === "companyId" && e.newValue !== lastCompanyId) {
      // 同步更新 store（使用 action 确保同时清除 localStorage）
      store.setCompanyId(e.newValue || null)
      lastCompanyId = e.newValue

      setTimeout(() => {
        console.log("storage event companyId changed, reinitialize i18n")
        reinitializeI18n(i18n)
      }, 100)
    }
  })
}

/**
 * 监听路由变化并重新检查配置
 * @param {Object} i18n - i18n实例
 * @param {Object} router - 路由实例
 */
function watchRouteChange(i18n, router) {
  router.beforeEach(async (to, from) => {
    // 检查是否需要重新获取配置
    const shouldRecheckConfig = shouldRecheckConfigOnRouteChange(to, from)
    if (shouldRecheckConfig) {
      try {
        // // 根据当前路由来源选择配置方法
        // if (isFromPayRoute()) {
        //   await getConfigMain()
        // } else {
        //   await getConfig()
        // }

        // 重新初始化i18n配置（内部已有防重复执行机制）
        await reinitializeI18n(i18n, true)
      } catch (error) {
        console.error(error)
      }
    }
  })
}

/**
 * 判断是否需要在路由切换时重新检查配置
 */
function shouldRecheckConfigOnRouteChange(to, from) {
  // 如果 from 不存在或者是空对象，说明是首次加载或刷新，不需要重新检查配置
  if (!from || !from.path || !from.name) {
    return false
  }

  // 如果 from 和 to 是同一个路由，说明是刷新，不需要重新检查配置
  if (from.path === to.path && from.name === to.name) {
    return false
  }

  // 从主路由切换到pay相关路由
  const toPayRoute =
    to.path.includes("/pay") ||
    to.name === "paypage" ||
    to.name === "paypagesec"
  const fromMainRoute =
    from.path === "/" ||
    from.path === "/home" ||
    from.name === "homepage" ||
    from.name === "home"

  // 从pay相关路由切换到主路由
  const fromPayRoute =
    from.path.includes("/pay") ||
    from.name === "paypage" ||
    from.name === "paypagesec"
  const toMainRoute =
    to.path === "/" ||
    to.path === "/home" ||
    to.name === "homepage" ||
    to.name === "home"

  // 如果路由类型发生变化，需要重新检查配置
  return (toPayRoute && fromMainRoute) || (toMainRoute && fromPayRoute)
}

/**
 * 异步设置 i18n
 * @returns {Promise<Object>} i18n实例
 */
async function setupI18n() {
  console.log("setupI18n111")
  const messages = await loadLanguagePackages()
  const store = platformStore()
  // 优先使用已保存的语言设置，如果没有则使用浏览器语言
  const savedLang = store.lang
  const browserLang = navigator.language.toLowerCase()
  const defaultLang = savedLang || browserLang

  const i18n = createI18n({
    legacy: false,
    locale: defaultLang,
    messages: messages,
    silentTranslationWarn: true,
    silentFallbackWarn: true,
    warnHtmlMessage: false, // 关闭 HTML 警告
    missingWarn: false,
    fallbackWarn: false,
  })

  // 监听companyId变化
  watchCompanyIdChange(i18n)

  return i18n
}

const baseLocaleMessagesMap = {}

function updateI18n(languageUpdate, i18n) {
  const locale = i18n.locale.value
  if (!baseLocaleMessagesMap[locale]) {
    baseLocaleMessagesMap[locale] = { ...i18n.getLocaleMessage(locale) } // 存最原始的key值
  }

  const updatedMessages = {
    ...baseLocaleMessagesMap[locale],
    ...languageUpdate,
  }

  i18n.setLocaleMessage(locale, updatedMessages)
}

function resetI18n(i18n) {
  const locale = i18n.locale.value
  if (!baseLocaleMessagesMap[locale]) return

  i18n.setLocaleMessage(locale, {
    ...baseLocaleMessagesMap[locale],
  })
}

export { reloadLanguagePack, watchRouteChange, updateI18n, resetI18n }
export default setupI18n
